import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { type VariantProps } from "class-variance-authority";
import { Check } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { tagVariants } from "./TagChip";
import { TagChips } from "./TagChips";
import type { Tag } from "./TagTypes";

export enum Delimiter {
  Comma = ",",
  Enter = "Enter",
}

export type { Tag } from "./TagTypes";

function defaultGenerateTagId(): string {
  return crypto.getRandomValues(new Uint32Array(1))[0].toString();
}

type OmittedInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "value"
>;

export interface TagInputProps
  extends OmittedInputProps,
    VariantProps<typeof tagVariants> {
  placeholder?: string;
  tags: Tag[];
  setTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  /** Suggestions shown in the command palette; may be empty. */
  autocompleteOptions?: Tag[];
  maxTags?: number;
  minTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  onTagAdd?: (tag: string) => void;
  onTagRemove?: (tag: string) => void;
  allowDuplicates?: boolean;
  validateTag?: (tag: string) => boolean;
  delimiter?: Delimiter;
  placeholderWhenFull?: string;
  sortTags?: boolean;
  delimiterList?: string[];
  truncate?: number;
  minLength?: number;
  maxLength?: number;
  value?: string | number | readonly string[] | { id: string; text: string }[];
  direction?: "row" | "column";
  onInputChange?: (value: string) => void;
  customTagRenderer?: (tag: Tag, isActiveTag: boolean) => React.ReactNode;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onTagClick?: (tag: Tag) => void;
  draggable?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  restrictTagsToAutocompleteOptions?: boolean;
  activeTagIndex: number | null;
  setActiveTagIndex: React.Dispatch<React.SetStateAction<number | null>>;
  addOnPaste?: boolean;
  addTagsOnBlur?: boolean;
  generateTagId?: () => string;
}

const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (props, _ref) => {
    const {
      id,
      placeholder,
      tags,
      setTags,
      variant,
      size,
      shape = "pill",
      autocompleteOptions,
      maxTags,
      delimiter = Delimiter.Comma,
      onTagAdd,
      onTagRemove,
      allowDuplicates,
      validateTag,
      placeholderWhenFull = "Max tags reached",
      sortTags,
      delimiterList,
      truncate,
      borderStyle,
      textCase,
      interaction,
      animation,
      textStyle,
      minLength,
      maxLength,
      direction = "row",
      onInputChange,
      customTagRenderer,
      onFocus,
      onBlur,
      onTagClick,
      draggable: _draggable = false,
      inputProps: rawInputProps = {},
      restrictTagsToAutocompleteOptions,
      addTagsOnBlur = false,
      activeTagIndex,
      setActiveTagIndex,
      disabled = false,
      addOnPaste = false,
      generateTagId = defaultGenerateTagId,
    } = props;

    const {
      onKeyDown: inputPropsOnKeyDown,
      onFocus: inputPropsOnFocus,
      onBlur: inputPropsOnBlur,
      className: inputPropsClassName,
      ...restInputProps
    } = rawInputProps;

    const [inputValue, setInputValue] = useState("");
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerContainerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const popoverContentRef = useRef<HTMLDivElement | null>(null);

    const filteredAutocompleteOptions = useMemo(() => {
      return (autocompleteOptions || []).filter((option) =>
        option.text
          .toLowerCase()
          .includes(inputValue ? inputValue.toLowerCase() : ""),
      );
    }, [inputValue, autocompleteOptions]);

    useEffect(() => {
      const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
        if (
          isPopoverOpen &&
          triggerContainerRef.current &&
          popoverContentRef.current &&
          !triggerContainerRef.current.contains(event.target as Node) &&
          !popoverContentRef.current.contains(event.target as Node)
        ) {
          setIsPopoverOpen(false);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }, [isPopoverOpen]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          inputRef.current?.focus();
          setIsPopoverOpen(open);
        }
      },
      [inputFocused],
    );

    if (
      (maxTags !== undefined && maxTags < 0) ||
      (props.minTags !== undefined && props.minTags < 0)
    ) {
      console.warn("maxTags and minTags cannot be less than 0");
      return null;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSelectedIndex(-1);
      if (addOnPaste && newValue.includes(delimiter)) {
        const splitValues = newValue
          .split(delimiter)
          .map((v) => v.trim())
          .filter((v) => v);

        // Accumulate on a local copy so duplicate/maxTags checks see tags added
        // earlier in this paste batch (the render closure's `tags` would be stale in a loop).
        let workingTags = [...tags];
        const addedTagTexts: string[] = [];

        for (const value of splitValues) {
          if (!value) {
            continue;
          }

          const newTagText = value.trim();

          // Check if the tag is in the autocomplete options if restrictTagsToAutocomplete is true
          if (
            restrictTagsToAutocompleteOptions &&
            !autocompleteOptions?.some((option) => option.text === newTagText)
          ) {
            console.warn("Tag not allowed as per autocomplete options");
            continue;
          }

          if (validateTag && !validateTag(newTagText)) {
            console.warn("Invalid tag as per validateTag");
            continue;
          }

          if (minLength && newTagText.length < minLength) {
            console.warn(`Tag "${newTagText}" is too short`);
            continue;
          }

          if (maxLength && newTagText.length > maxLength) {
            console.warn(`Tag "${newTagText}" is too long`);
            continue;
          }

          if (
            !allowDuplicates &&
            workingTags.some((tag) => tag.text === newTagText)
          ) {
            console.warn(`Duplicate tag "${newTagText}" not added`);
            continue;
          }

          if (maxTags !== undefined && workingTags.length >= maxTags) {
            console.warn("Reached the maximum number of tags allowed");
            continue;
          }

          const newTag: Tag = {
            id: generateTagId(),
            text: newTagText,
          };
          workingTags = [...workingTags, newTag];
          addedTagTexts.push(newTagText);
        }

        if (addedTagTexts.length > 0) {
          setTags(workingTags);
          for (const text of addedTagTexts) {
            onTagAdd?.(text);
          }
        }
        setInputValue("");
      } else {
        setInputValue(newValue);
      }
      onInputChange?.(newValue);
    };

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (triggerContainerRef.current) {
        setIsPopoverOpen(true);
      }
      if (isPopoverOpen) {
        setInputFocused(true);
      }
      setActiveTagIndex(null);
      onFocus?.(event);
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setInputFocused(false);
      if (!isPopoverOpen) {
        setIsPopoverOpen(false);
      }

      if (addTagsOnBlur && inputValue.trim()) {
        const newTagText = inputValue.trim();

        if (validateTag && !validateTag(newTagText)) {
          return;
        }

        if (minLength && newTagText.length < minLength) {
          console.warn("Tag is too short");
          return;
        }

        if (maxLength && newTagText.length > maxLength) {
          console.warn("Tag is too long");
          return;
        }

        if (
          (allowDuplicates || !tags.some((tag) => tag.text === newTagText)) &&
          (maxTags === undefined || tags.length < maxTags)
        ) {
          const newTagId = generateTagId();
          setTags([...tags, { id: newTagId, text: newTagText }]);
          onTagAdd?.(newTagText);
          setInputValue("");
        }
      }

      onBlur?.(event);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        delimiterList
          ? delimiterList.includes(e.key)
          : e.key === delimiter || e.key === Delimiter.Enter
      ) {
        e.preventDefault();
        const newTagText = inputValue.trim();

        // Check if the tag is in the autocomplete options if restrictTagsToAutocomplete is true
        if (
          restrictTagsToAutocompleteOptions &&
          !autocompleteOptions?.some((option) => option.text === newTagText)
        ) {
          // error
          return;
        }

        if (validateTag && !validateTag(newTagText)) {
          return;
        }

        if (minLength && newTagText.length < minLength) {
          console.warn("Tag is too short");
          // error
          return;
        }

        // Validate maxLength
        if (maxLength && newTagText.length > maxLength) {
          // error
          console.warn("Tag is too long");
          return;
        }

        const newTagId = generateTagId();

        if (
          newTagText &&
          (allowDuplicates || !tags.some((tag) => tag.text === newTagText)) &&
          (maxTags === undefined || tags.length < maxTags)
        ) {
          setTags([...tags, { id: newTagId, text: newTagText }]);
          onTagAdd?.(newTagText);
        }
        setInputValue("");
      } else {
        switch (e.key) {
          case "Delete":
            if (activeTagIndex !== null) {
              e.preventDefault();
              const newTags = [...tags];
              newTags.splice(activeTagIndex, 1);
              setTags(newTags);
              setActiveTagIndex((prev) =>
                newTags.length === 0
                  ? null
                  : prev! >= newTags.length
                    ? newTags.length - 1
                    : prev,
              );
              onTagRemove?.(tags[activeTagIndex].text);
            }
            break;
          case "Backspace":
            if (activeTagIndex !== null) {
              e.preventDefault();
              const newTags = [...tags];
              newTags.splice(activeTagIndex, 1);
              setTags(newTags);
              setActiveTagIndex((prev) => (prev! === 0 ? null : prev! - 1));
              onTagRemove?.(tags[activeTagIndex].text);
            }
            break;
          case "ArrowRight":
            e.preventDefault();
            if (activeTagIndex === null) {
              setActiveTagIndex(0);
            } else {
              setActiveTagIndex((prev) =>
                prev! + 1 >= tags.length ? 0 : prev! + 1,
              );
            }
            break;
          case "ArrowLeft":
            e.preventDefault();
            if (activeTagIndex === null) {
              setActiveTagIndex(tags.length - 1);
            } else {
              setActiveTagIndex((prev) =>
                prev! === 0 ? tags.length - 1 : prev! - 1,
              );
            }
            break;
          case "Home":
            e.preventDefault();
            setActiveTagIndex(0);
            break;
          case "End":
            e.preventDefault();
            setActiveTagIndex(tags.length - 1);
            break;
        }
      }
    };

    const toggleSuggestionTag = (option: Tag) => {
      const index = tags.findIndex((tag) => tag.text === option.text);

      if (index >= 0) {
        const newTags = tags.filter((_, i) => i !== index);
        setTags(newTags);
        onTagRemove?.(option.text);
      } else {
        if (!allowDuplicates && tags.some((tag) => tag.text === option.text)) {
          return;
        }

        if (maxTags === undefined || tags.length < maxTags) {
          setTags([...tags, option]);
          setInputValue("");
          onTagAdd?.(option.text);
        }
      }
      setSelectedIndex(-1);
    };

    const handleComposedKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (isPopoverOpen) {
        switch (e.key) {
          case "ArrowUp":
            e.preventDefault();
            setSelectedIndex((prevIndex) =>
              prevIndex <= 0
                ? filteredAutocompleteOptions.length - 1
                : prevIndex - 1,
            );
            return;
          case "ArrowDown":
            e.preventDefault();
            setSelectedIndex((prevIndex) =>
              prevIndex === filteredAutocompleteOptions.length - 1
                ? 0
                : prevIndex + 1,
            );
            return;
          case "Enter":
            if (
              selectedIndex !== -1 &&
              filteredAutocompleteOptions[selectedIndex]
            ) {
              e.preventDefault();
              toggleSuggestionTag(filteredAutocompleteOptions[selectedIndex]);
              return;
            }
            break;
        }
      }
      handleKeyDown(e);
    };

    const removeTag = (idToRemove: string) => {
      setTags(tags.filter((tag) => tag.id !== idToRemove));
      onTagRemove?.(tags.find((tag) => tag.id === idToRemove)?.text || "");
    };

    const displayedTags = sortTags
      ? [...tags].sort((a, b) => a.text.localeCompare(b.text))
      : tags;

    const truncatedTags = truncate
      ? displayedTags.map((tag) => ({
          id: tag.id,
          text:
            tag.text?.length > truncate
              ? `${tag.text.substring(0, truncate)}...`
              : tag.text,
        }))
      : displayedTags;

    return (
      <div className="w-full">
        <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
          <PopoverAnchor asChild>
            <div
              className="relative flex h-full w-full items-center rounded-md border bg-transparent"
              ref={triggerContainerRef}
            >
              <div className="flex min-w-0 flex-1 flex-row flex-wrap items-center gap-1 p-1 h-fit w-full bg-background text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                <TagChips
                  tags={truncatedTags}
                  customTagRenderer={customTagRenderer}
                  variant={variant}
                  size={size}
                  shape={shape}
                  borderStyle={borderStyle}
                  textCase={textCase}
                  interaction={interaction}
                  animation={animation}
                  textStyle={textStyle}
                  onTagClick={onTagClick}
                  onRemoveTag={removeTag}
                  direction={direction}
                  activeTagIndex={activeTagIndex}
                  setActiveTagIndex={setActiveTagIndex}
                  disabled={disabled}
                />
                <Input
                  {...restInputProps}
                  ref={inputRef}
                  id={id}
                  type="text"
                  placeholder={
                    maxTags !== undefined && tags.length >= maxTags
                      ? placeholderWhenFull
                      : placeholder
                  }
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    handleComposedKeyDown(e);
                    inputPropsOnKeyDown?.(e);
                  }}
                  onFocus={(e) => {
                    handleInputFocus(e);
                    inputPropsOnFocus?.(e);
                  }}
                  onBlur={(e) => {
                    handleInputBlur(e);
                    inputPropsOnBlur?.(e);
                  }}
                  className={cn(
                    "border-0 h-5 bg-transparent focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 flex-1 min-w-0 px-1",
                    inputPropsClassName,
                  )}
                  autoComplete="off"
                  disabled={
                    disabled ||
                    (maxTags !== undefined && tags.length >= maxTags)
                  }
                />
              </div>

              <PopoverTrigger asChild ref={triggerRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  role="combobox"
                  type="button"
                  className="shrink-0 hover:bg-transparent"
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn(
                      "lucide lucide-chevron-down h-4 w-4 shrink-0 opacity-50 transition-transform",
                      isPopoverOpen ? "rotate-180" : "rotate-0",
                    )}
                  >
                    <path d="m6 9 6 6 6-6"></path>
                  </svg>
                </Button>
              </PopoverTrigger>
            </div>
          </PopoverAnchor>
          <PopoverContent
            ref={popoverContentRef}
            side="bottom"
            align="start"
            forceMount
            className={cn("p-0 min-w-0")}
            style={{
              width: "var(--radix-popover-trigger-width)",
              maxWidth: "var(--radix-popover-trigger-width)",
            }}
          >
            <Command
              shouldFilter={false}
              className="w-full rounded-md border-0 shadow-none"
            >
              <CommandList className="max-h-[300px] w-full">
                {filteredAutocompleteOptions.length > 0 ? (
                  <CommandGroup heading="Suggestions" className="w-full p-1">
                    {filteredAutocompleteOptions.map((option, index) => {
                      const isKeyboardSelected = index === selectedIndex;
                      const isApplied = tags.some(
                        (tag) => tag.text === option.text,
                      );
                      return (
                        <CommandItem
                          key={option.id}
                          value={option.text}
                          className={cn(
                            "w-full",
                            isKeyboardSelected &&
                              "bg-accent text-accent-foreground",
                          )}
                          onSelect={() => toggleSuggestionTag(option)}
                        >
                          <span className="flex-1">{option.text}</span>
                          {isApplied ? (
                            <Check className="size-4 shrink-0" aria-hidden />
                          ) : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>No results found.</CommandEmpty>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

TagInput.displayName = "TagInput";

export { TagInput };
